/**
 * Автопродолжение списков в обычной <textarea>.
 *
 * Подключается через хук `hooks/useAutoList.js`: <textarea ref={useAutoList('field')} />.
 *
 * Пользователь пишет «1. Первая строка» и жмёт Enter — на новой строке
 * автоматом появляется «2. ». То же для буллетов: «- », «* », «• ».
 * Enter на пустом пункте («3. » без текста) — выход из списка: маркер
 * стирается, курсор остаётся на пустой строке.
 *
 * Нумерованный список перенумеровывается: Enter в середине блока сдвигает
 * номера всех последующих пунктов, выход из списка — подтягивает их обратно.
 * Блок обрывается на первой строке, которая не является нумерованным пунктом
 * с тем же отступом (пустая строка, буллет, вложенный подсписок).
 *
 * Правки вносятся через execCommand('insertText') — это сохраняет нативный
 * undo (Cmd+Z) и порождает событие input, которое React ловит как onChange.
 * Перенумерация переписывает только хвост блока, а не всё поле, поэтому undo
 * выживает и в ней. Если execCommand недоступен — фолбэк через нативный
 * сеттер value (undo в этой ветке теряется).
 *
 * Текста в UI не добавляет (маркеры «1.» / «-» языконезависимы), поэтому
 * i18n не затрагивает.
 */

// «  12) текст» → отступ, номер, разделитель, пробелы, содержимое
const NUMBERED = /^([ \t]*)(\d{1,9})([.)])([ \t]+)(.*)$/;
// «  - текст» → отступ, маркер, пробелы, содержимое
const BULLETED = /^([ \t]*)([-*•])([ \t]+)(.*)$/;

/** Разобрать строку как пункт списка. Вернёт null, если это не список. */
export function parseListLine(line) {
  const n = line.match(NUMBERED);
  if (n) {
    return {
      type: 'numbered',
      indent: n[1],
      num: parseInt(n[2], 10),
      delim: n[3],
      content: n[5],
      markerLen: n[1].length + n[2].length + n[3].length + n[4].length,
    };
  }
  const b = line.match(BULLETED);
  if (b) {
    return {
      type: 'bullet',
      indent: b[1],
      bullet: b[2],
      content: b[4],
      markerLen: b[1].length + b[2].length + b[3].length,
    };
  }
  return null;
}

/**
 * Собрать подряд идущие нумерованные пункты того же уровня, начиная со строки
 * в позиции startIndex. blockEnd — индекс конца последнего собранного пункта.
 */
function collectNumberedBlock(value, startIndex, indent) {
  const contents = [];
  let blockEnd = startIndex;
  let pos = startIndex;
  while (pos <= value.length) {
    let end = value.indexOf('\n', pos);
    if (end === -1) end = value.length;
    const parsed = parseListLine(value.slice(pos, end));
    if (!parsed || parsed.type !== 'numbered' || parsed.indent !== indent) break;
    contents.push(parsed.content);
    blockEnd = end;
    if (end === value.length) break;
    pos = end + 1;
  }
  return { contents, blockEnd };
}

/** Заменить диапазон [from, to) на text и поставить курсор в caret. */
function replaceRange(el, from, to, text, caret) {
  el.setSelectionRange(from, to);
  let ok = false;
  try {
    ok = text
      ? document.execCommand('insertText', false, text)
      : document.execCommand('delete');
  } catch {
    ok = false;
  }

  if (!ok) {
    // Фолбэк: пишем через нативный сеттер, иначе React не увидит изменение value
    const next = el.value.slice(0, from) + text + el.value.slice(to);
    const setter = Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype, 'value',
    )?.set;
    if (setter) setter.call(el, next);
    else el.value = next;
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }

  if (caret != null) el.setSelectionRange(caret, caret);
}

/**
 * Продолжить список в момент вставки перевода строки.
 *
 * ⚠️ Вызывается из `beforeinput` (inputType = insertLineBreak), а НЕ из keydown.
 * Причина — системный голосовой ввод: macOS Dictation держит надиктованный текст
 * активной композицией, софт-клавиатуры шлют `keyCode 229` и `key: 'Unidentified'`.
 * На keydown это неотличимо от IME-композиции, и любой honest-предохранитель
 * («не мешать IME») глушил автосписок вместе с диктовкой.
 *
 * У beforeinput такой проблемы нет: подтверждение IME-кандидата приходит как
 * insertCompositionText, реальный перевод строки — как insertLineBreak. Ловим
 * только второе, поэтому IME в безопасности без всяких эвристик.
 *
 * Побочный эффект смены механизма: Shift+Enter больше не «сырой перенос» —
 * он неотличим от Enter на уровне inputType. Выход из списка — Enter на пустом
 * пункте, как в Notion.
 *
 * @param {HTMLTextAreaElement} el
 * @param {{ prevent?: () => void, onContinue?: (type: string) => void, onExit?: (type: string) => void }} opts
 *        prevent — вызывается ПЕРЕД правкой, если продолжение списка состоится
 * @returns {boolean} true, если список продолжен (перевод строки надо отменить)
 */
export function applyListBreak(el, opts = {}) {
  if (!el || typeof el.selectionStart !== 'number') return false;
  const { selectionStart: start, selectionEnd: end, value } = el;
  if (start !== end) return false; // есть выделение — не мешаем

  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  let lineEnd = value.indexOf('\n', start);
  if (lineEnd === -1) lineEnd = value.length;

  const item = parseListLine(value.slice(lineStart, lineEnd));
  if (!item) return false;
  // Курсор внутри самого маркера («1|. текст») — это не продолжение списка
  if (start < lineStart + item.markerLen) return false;

  // Хвост нумерованного блока после текущей строки — его придётся перенумеровать
  const follow = item.type === 'numbered' && lineEnd < value.length
    ? collectNumberedBlock(value, lineEnd + 1, item.indent)
    : { contents: [], blockEnd: lineEnd };

  opts.prevent?.();

  // ── Пустой пункт → выходим из списка, маркер стираем ──────────────────────
  if (item.content.trim() === '') {
    if (follow.contents.length) {
      // Пункт исчезает — последующие подтягиваются на его номер
      let n = item.num - 1;
      const text = follow.contents
        .map(c => `\n${item.indent}${++n}${item.delim} ${c}`)
        .join('');
      replaceRange(el, lineStart, follow.blockEnd, text, lineStart);
    } else {
      replaceRange(el, lineStart, lineEnd, '', lineStart);
    }
    opts.onExit?.(item.type);
    return true;
  }

  // ── Буллет: нумеровать нечего, просто вставляем маркер ────────────────────
  if (item.type === 'bullet') {
    const marker = `${item.indent}${item.bullet} `;
    replaceRange(el, start, start, `\n${marker}`, start + 1 + marker.length);
    opts.onContinue?.(item.type);
    return true;
  }

  // ── Нумерованный: новый пункт + сдвиг номеров хвоста ──────────────────────
  const tail = value.slice(start, lineEnd); // остаток строки уезжает на новый пункт
  const marker = `${item.indent}${item.num + 1}${item.delim} `;
  let n = item.num + 1;
  let text = `\n${marker}${tail}`;
  for (const c of follow.contents) text += `\n${item.indent}${++n}${item.delim} ${c}`;

  const replaceTo = follow.contents.length ? follow.blockEnd : lineEnd;
  replaceRange(el, start, replaceTo, text, start + 1 + marker.length);
  opts.onContinue?.(item.type);
  return true;
}
